<template>
  <div class="modal fade" ref="fieldsModal" data-bs-backdrop="static" data-bs-keyboard="false">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Manage Document Fields</h5>
          <button class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <button class="btn btn-success btn-sm mb-3" @click="toggleForm">
            {{ showForm ? '✕ Cancel' : '+ Add New Field' }}
          </button>

          <!-- Add Field Form -->
          <div v-if="showForm" class="card p-3 mb-3">
            <div class="row g-2">
              <div class="col-md-6">
                <label class="form-label">Field Name *</label>
                <input
                  v-model="newField.name"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="e.g. email_address"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label">Label *</label>
                <input
                  v-model="newField.label"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="e.g. Email Address"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label">Field Type *</label>
                <select v-model="newField.field_type" class="form-control form-control-sm">
                  <option value="text">Text</option>
                  <option value="email">Email</option>
                  <option value="date">Date</option>
                  <option value="number">Number</option>
                  <option value="amount">Amount</option>
                  <option value="textarea">Textarea</option>
                  <option value="select">Select</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Placeholder</label>
                <input
                  v-model="newField.placeholder"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="e.g. Enter Company Name"
                />
              </div>
              <div v-if="newField.field_type === 'select'" class="col-12">
                <label class="form-label">Options (comma separated)</label>
                <input
                  v-model="newField.options"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Option1, Option2, Option3"
                />
              </div>
              <div class="col-12">
                <label class="form-label">Description</label>
                <input
                  v-model="newField.description"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="e.g. Enter Description"
                />
              </div>
              <div class="col-12">
                <button
                  class="btn btn-primary btn-sm"
                  @click="saveField"
                  :disabled="!newField.name || !newField.label"
                >
                  {{ editingFieldId ? 'Update Field' : 'Add Field' }}
                </button>
                <button
                  class="btn btn-secondary btn-sm ms-2"
                  @click="toggleForm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          <!-- Fields List -->
         <div class="table-responsive" style="max-height: 200px; overflow-y: auto;">
             <table class="table table-sm ">
              <thead class="table-light">
                <tr>
                  <th>Name</th>
                  <th>Label</th>
                  <th>Type</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="field in fields" :key="field.id">
                  <td>{{ field.name }}</td>
                  <td>{{ field.label }}</td>
                  <td>
                    <span class="badge bg-info">{{ field.field_type }}</span>
                  </td>
                  <td>
                    <button class="btn-pencil" v-if="permissions.includes('document_template.write')"
                       @click="editField(field)" title="Edit">
                      <i class="fa fa-pencil fs-5"></i>
                    </button>
                    <button class="btn-trash mx-1" v-if="permissions.includes('document_template.delete')"
                       @click="deleteField(field.id)" title="Delete">
                      <i class="fa fa-trash fs-5"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="fields.length === 0" class="alert alert-info">
              No fields created yet. Create one to get started.
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import user from "@/ajax/user";

export default {
  name: "DocumentFieldsManager",

  data() {
    return {
      fields: [],
      showForm: false,
      permissions: window.localStorage.getItem("permissions"),
      editingFieldId: null,
      newField: {
        name: "",
        label: "",
        field_type: "text",
        placeholder: "",
        options: "",
        description: "",
      },
    };
  },

  methods: {
    openModal() {
      this.loadFields();
      const modal = new (require("bootstrap")).Modal(this.$refs.fieldsModal);
      modal.show();
    },

    closeModal() {
      const modal = require("bootstrap").Modal.getInstance(this.$refs.fieldsModal);
      if (modal) modal.hide();
    },

    async loadFields() {
      try {
        const response = await user.getDocumentFields();
        this.fields = response.data.data || [];
      } catch (error) {
        console.error("Error loading fields:", error);
        this.$swal("Error", "Failed to load fields", "error");
      }
    },

    toggleForm() {
      if (this.showForm) {
        this.resetForm();
      }
      this.showForm = !this.showForm;
    },

    editField(field) {
      this.editingFieldId = field.id;
      this.newField = {
        name: field.name,
        label: field.label,
        field_type: field.field_type,
        placeholder: field.placeholder || "",
        options: field.options || "",
        description: field.description || "",
      };
      this.showForm = true;
      window.scrollTo(0, 0);
    },

    resetForm() {
      this.editingFieldId = null;
      this.newField = {
        name: "",
        label: "",
        field_type: "text",
        placeholder: "",
        options: "",
        description: "",
      };
    },

    async saveField() {
      if (!this.newField.name || !this.newField.label) {
        this.$swal("Warning", "Name and Label are required", "warning");
        return;
      }

      try {
        let options = this.newField.options;
        if (options && typeof options === "string") {
          options = JSON.stringify(
            options.split(",").map((o) => o.trim())
          );
        }

        const payload = {
          ...this.newField,
          options: options,
        };

        if (this.editingFieldId) {
          // Update existing field
          await user.updateDocumentField(this.editingFieldId, payload);
          this.$swal("Success", "Field updated successfully", "success");
        } else {
          // Create new field
          await user.createDocumentField(payload);
          this.$swal("Success", "Field created successfully", "success");
        }
        
        // Reset form
        this.resetForm();
        this.showForm = false;
        this.loadFields();
      } catch (error) {
        console.error("Error saving field:", error);
        const msg = error.response?.data?.detail || "Failed to save field";
        this.$swal("Error", msg, "error");
      }
    },

    async deleteField(fieldId) {
      this.$swal({
        title: "Delete",
        text: "Are you sure you want to delete this field?",
        icon: "question",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#32a852",
        cancelButtonColor: "#f00",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.value || result.isConfirmed) {
          user.deleteDocumentField(fieldId)
            .then((res) => {
              this.$swal("Success", "Field deleted successfully", "success");
              this.loadFields();
            })
            .catch((error) => {
              console.error("Error deleting field:", error);
              this.$swal("Error", "Failed to delete field", "error");
            });
        }
      });
    },
  },
};
</script>
